/**
 * LQIP (low-quality image placeholder) primitives — shared by the vendor
 * upload path (which produces them) and buyer surfaces (which paint them).
 *
 * Why not BlurHash: it needs a decoder on the client (~2 KB JS plus a canvas
 * paint per tile) and a base-83 string that no CSS engine understands. On a
 * 360px Bangladeshi phone rendering 20 tiles that is measurable jank for a
 * placeholder nobody looks at for more than 300 ms.
 *
 * Instead we keep a 4x4 colour grid (16 hex values, ~112 bytes) and paint it
 * as sixteen overlapping radial gradients with a blur. The browser rasterises
 * it on the compositor, there is zero JavaScript at paint time, and it still
 * reads as a soft, recognisable shape of the photo — the same effect BlurHash
 * gives. `color` alone (dominant colour) is the graceful degradation.
 */

export type ImagePlaceholder = {
  /** Dominant colour, `#rrggbb`. Always present. */
  color: string;
  /** Row-major 4x4 grid of `#rrggbb`. Optional; enables the blurred preview. */
  grid?: string[];
};

const HEX = /^#[0-9a-f]{6}$/i;
export const PLACEHOLDER_GRID_SIZE = 4;

/** Narrow unknown JSON (from the database) to a usable placeholder. */
export function asPlaceholder(value: unknown): ImagePlaceholder | null {
  if (!value || typeof value !== "object") return null;
  const v = value as { color?: unknown; grid?: unknown };
  if (typeof v.color !== "string" || !HEX.test(v.color)) return null;
  const grid =
    Array.isArray(v.grid) &&
    v.grid.length === PLACEHOLDER_GRID_SIZE * PLACEHOLDER_GRID_SIZE &&
    v.grid.every((c) => typeof c === "string" && HEX.test(c))
      ? (v.grid as string[])
      : undefined;
  return grid ? { color: v.color, grid } : { color: v.color };
}

/** Placeholder for the image at `index` of an aligned per-image array. */
export function placeholderAt(
  placeholders: unknown,
  index: number,
): ImagePlaceholder | null {
  if (!Array.isArray(placeholders)) return null;
  return asPlaceholder(placeholders[index]);
}

/**
 * Inline style that paints the placeholder.
 *
 * The grid becomes 16 radial gradients positioned at cell centres; each fades
 * out by ~40% of the tile so neighbours blend. A CSS `filter: blur()` on the
 * layer smooths the remaining banding. Falls back to a flat dominant colour.
 */
export function placeholderStyle(
  ph: ImagePlaceholder | null | undefined,
): React.CSSProperties | undefined {
  if (!ph) return undefined;
  if (!ph.grid) return { backgroundColor: ph.color };

  const n = PLACEHOLDER_GRID_SIZE;
  const step = 100 / n;
  const layers: string[] = [];
  for (let y = 0; y < n; y += 1) {
    for (let x = 0; x < n; x += 1) {
      const cx = step * x + step / 2;
      const cy = step * y + step / 2;
      const c = ph.grid[y * n + x]!;
      layers.push(
        `radial-gradient(circle at ${cx.toFixed(1)}% ${cy.toFixed(1)}%, ${c} 0%, transparent ${(
          step * 1.6
        ).toFixed(1)}%)`,
      );
    }
  }
  return {
    backgroundColor: ph.color,
    backgroundImage: layers.join(", "),
    // Painting the blur on this element keeps it off the <img> above it.
    filter: "blur(12px)",
    transform: "scale(1.15)",
  };
}

/** Average of a set of `#rrggbb` values — used to derive `color` from a grid. */
export function averageHex(colors: string[]): string {
  if (!colors.length) return "#e5e7eb";
  let r = 0;
  let g = 0;
  let b = 0;
  for (const c of colors) {
    r += parseInt(c.slice(1, 3), 16);
    g += parseInt(c.slice(3, 5), 16);
    b += parseInt(c.slice(5, 7), 16);
  }
  const n = colors.length;
  const to = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v / n)))
      .toString(16)
      .padStart(2, "0");
  return `#${to(r)}${to(g)}${to(b)}`;
}
